/**
 * Scheduling Service — Phase 1 (Read-Only)
 *
 * Fetches booking data from the Projects API adapter endpoint which
 * normalises data from the connected resource scheduler (EBScheduler).
 *
 * In demo/tour mode the existing mock data is returned so the UI can
 * be developed without a live backend.  In live mode the real API is called.
 */

import { apiFetch } from '@/lib/api-error-handler';
import { getDataMode, getApiBaseUrl } from '@/lib/data-service';
import {
  mockSchedulingBookings,
  mockSchedulerConnection,
  mockResourceRequests,
  type SchedulingBooking,
  type ResourceRequest,
} from '@/lib/mock-scheduling-data';

// ---------------------------------------------------------------------------
// Response types — resource request list (GET)
// ---------------------------------------------------------------------------

export interface ResourceRequestResponse {
  requestId: string;
  roleName: string;
  hoursRequired: number;
  desiredStartDate: string;
  desiredEndDate?: string;
  priority: string;
  status: string;
  notes?: string;
  raisedAt: string;
  assignedTo?: string;
}

// ---------------------------------------------------------------------------
// Request types — resource request creation
// ---------------------------------------------------------------------------

export interface CreateResourceRequestPayload {
  roleId?: string;
  roleName: string;
  hoursRequired: number;
  desiredStartDate: string; // YYYY-MM-DD
  priority: string; // nice-to-have | important | critical
  notes?: string;
}

export interface CreateResourceRequestResponse {
  requestId: string | null;
  status: string;
  message: string | null;
}

// ---------------------------------------------------------------------------
// Response types (mirror the backend SchedulingBookingsResponse DTO)
// ---------------------------------------------------------------------------

export interface SchedulingBookingsResponse {
  connectionStatus: 'connected' | 'not_configured' | 'error';
  schedulerName: string | null;
  bookings: SchedulingBooking[];
}

// ---------------------------------------------------------------------------
// Service function
// ---------------------------------------------------------------------------

/**
 * Fetch normalised scheduling bookings for a project.
 *
 * - demo / tour → returns mock data immediately
 * - live        → calls GET /api/projects/{projectId}/scheduling/bookings
 */
export async function fetchSchedulingBookings(
  projectId: string,
): Promise<SchedulingBookingsResponse> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    // Simulate a small network delay so loading states are visible during dev
    await new Promise((resolve) => setTimeout(resolve, 300));
    return {
      connectionStatus: mockSchedulerConnection.status === 'connected' ? 'connected' : 'not_configured',
      schedulerName: mockSchedulerConnection.displayName,
      bookings: mockSchedulingBookings,
    };
  }

  // Live mode — call the real backend adapter endpoint
  const url = `${getApiBaseUrl()}/projects/${projectId}/scheduling/bookings`;
  const data = await apiFetch<SchedulingBookingsResponse>(url);
  return data;
}

// ---------------------------------------------------------------------------
// Create resource request
// ---------------------------------------------------------------------------

/**
 * Create a resource request (unallocated demand booking) in the connected scheduler.
 *
 * - demo / tour → simulates success after a short delay
 * - live        → calls POST /api/projects/{projectId}/scheduling/requests
 */
/**
 * Fetch all resource requests for a project.
 *
 * - demo / tour → returns mock data immediately
 * - live        → calls GET /api/projects/{projectId}/scheduling/requests
 */
export async function fetchResourceRequests(
  projectId: string,
): Promise<ResourceRequestResponse[]> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise((resolve) => setTimeout(resolve, 300));
    // Map mock data to the response shape
    return mockResourceRequests.map((r: ResourceRequest) => ({
      requestId: r.requestId,
      roleName: r.roleName,
      hoursRequired: r.hoursRequired,
      desiredStartDate: r.startDate,
      desiredEndDate: r.endDate,
      priority: r.priority,
      status: r.status,
      notes: r.notes,
      raisedAt: r.raisedAt,
      assignedTo: r.assignedTo,
    }));
  }

  const url = `${getApiBaseUrl()}/projects/${projectId}/scheduling/requests`;
  const data = await apiFetch<ResourceRequestResponse[]>(url);
  return data;
}

export async function createResourceRequest(
  projectId: string,
  payload: CreateResourceRequestPayload,
): Promise<CreateResourceRequestResponse> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise((resolve) => setTimeout(resolve, 800));
    return {
      requestId: `mock-${Date.now()}`,
      status: 'Pending',
      message: `Resource request created for ${payload.roleName}.`,
    };
  }

  const url = `${getApiBaseUrl()}/projects/${projectId}/scheduling/requests`;
  const data = await apiFetch<CreateResourceRequestResponse>(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return data;
}
